/**
 * The intro tour's slides (Sept 2026): markup + a scripted animation for each.
 *
 * Ported 1:1 from the approved prototype, so each slide builds up exactly the
 * way it was signed off (the pill types, the card fills in word by word, like
 * the What's New demos). Imperative on purpose: these are short timed scripts
 * over static markup, which is simpler and smoother than re-rendering React
 * state every few milliseconds. Made-up people and amounts only, never the
 * user's data, and nothing here is saved.
 */

export type SceneCtx = {
  q: <T extends Element = HTMLElement>(sel: string) => T;
  qa: <T extends Element = HTMLElement>(sel: string) => NodeListOf<T>;
  /** Waits, honouring cancellation (throws when the slide is left). */
  later: (ms: number) => Promise<void>;
  /** Ticks the n-th detail under the headline. */
  tick: (n: number) => void;
  /** Runs when the slide is left (to stop anything still animating). */
  onLeave: (fn: () => void) => void;
};

export type FinishAction = "apple" | "google" | "email" | "login" | "done";

export type Slide = {
  key: string;
  /** Headline; slides without one draw their own full-screen layout. */
  h?: string;
  sub?: string;
  ticks?: string[];
  /** Part of the dotted progress (all but the splash and the last slide). */
  dots?: boolean;
  html: () => string;
  run: (c: SceneCtx) => Promise<void>;
};

import { startMeadow } from "./meadow";
import { BRAND } from "./brand-icons";

const I = (d: string, extra = "") => `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" ${extra}>${d}</svg>`;
export const IC = {
  receipt: I('<path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1Z"/><path d="M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8"/><path d="M12 17.5v-11"/>'),
  mic: I('<rect x="9" y="2" width="6" height="13" rx="3"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><path d="M12 19v3"/>'),
  up: I('<path d="m5 12 7-7 7 7"/><path d="M12 19V5"/>'),
  wallet: I('<path d="M19 7V4a1 1 0 0 0-1-1H5a2 2 0 0 0 0 4h15a1 1 0 0 1 1 1v4h-3a2 2 0 0 0 0 4h3a1 1 0 0 0 1-1v-2a1 1 0 0 0-1-1"/><path d="M3 5v14a2 2 0 0 0 2 2h15a1 1 0 0 0 1-1v-4"/>'),
  cal: I('<rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/>'),
  check: I('<path d="M20 6 9 17l-5-5"/>', 'stroke-width="3"'),
  check2: I('<path d="M20 6 9 17l-5-5"/>'),
  lock: I('<rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>'),
  repeat: I('<path d="m17 2 4 4-4 4"/><path d="M3 11v-1a4 4 0 0 1 4-4h14"/><path d="m7 22-4-4 4-4"/><path d="M21 13v1a4 4 0 0 1-4 4H3"/>'),
  copy: I('<rect x="8" y="8" width="14" height="14" rx="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/>'),
  undo: I('<path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/>'),
  camera: I('<path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z"/><circle cx="12" cy="13" r="3"/>'),
  swap: I('<path d="M8 3 4 7l4 4"/><path d="M4 7h16"/><path d="m16 21 4-4-4-4"/><path d="M20 17H4"/>'),
  apple: '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>',
  google: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>',
};

const LOGO = `<svg viewBox="0 0 32 32" fill="none" aria-hidden="true">
  <circle class="dot" cx="10" cy="8.8" r="1.5" fill="currentColor" style="transition-delay:.35s"/><path class="bar" d="M10 13.6V23.8" stroke="currentColor" stroke-width="3" stroke-linecap="round"/>
  <circle class="dot" cx="16" cy="8.8" r="1.5" fill="currentColor" style="transition-delay:.47s"/><path class="bar" d="M16 13.6V23.8" stroke="currentColor" stroke-width="3" stroke-linecap="round" style="transition-delay:.1s"/>
  <circle class="dot" cx="22" cy="8.8" r="1.5" fill="currentColor" style="transition-delay:.59s"/><path class="bar" d="M22 13.6V23.8" stroke="currentColor" stroke-width="3" stroke-linecap="round" style="transition-delay:.2s"/></svg>`;
const BARS = (anim: boolean) => `<span class="bars ${anim ? "anim" : ""}">${[7, 13, 18, 12, 6].map((h, i) => `<span style="height:${h}px;animation-delay:${i * 0.07}s;animation-duration:${0.45 + i * 0.1}s"></span>`).join("")}</span>`;

type PersonKey = "me" | "s" | "m" | "a";
const PEOPLE: Record<PersonKey, { n: string; c: string }> = {
  me: { n: "You", c: "" }, s: { n: "Sarah", c: "#A6674A" }, m: { n: "Marcus", c: "#7A3E32" }, a: { n: "Abhishek", c: "#9A4A2A" },
};
const av = (k: PersonKey, cls = "") => k === "me" ? `<span class="av me ${cls}">Y</span>` : `<span class="av ${cls}" style="background:${PEOPLE[k].c}">${PEOPLE[k].n[0]}</span>`;
const money = (n: number) => "$" + n.toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const nextFrame = (fn: () => void) => requestAnimationFrame(() => requestAnimationFrame(fn));

// An expense card with the same structure as quick-add-demo's ExampleCard.
const expenseCard = () => `
  <div class="card rise">
    <div class="head"><span class="hic">${IC.receipt}</span><span class="hl">Expense</span><span class="tagslot"></span></div>
    <p class="desc ph">Add a description</p>
    <div class="chips"><span class="chip ghost amt">+ Amount</span><span class="chip">${IC.wallet}Paid by you</span><span class="chip date">${IC.cal}<span>Today</span></span></div>
    <div class="box split">
      <div style="min-width:0"><p class="lbl">Split equally · <span class="n">1</span></p><div class="avs">${av("me")}</div><p class="names">You</p></div>
      <div><p class="lbl" style="text-align:right">Each pays</p><p class="each"><span style="color:var(--mfg)">—</span></p></div>
    </div>
  </div>`;

function cardApi(root: HTMLElement) {
  const q = (s: string) => root.querySelector(s) as HTMLElement;
  const people: PersonKey[] = ["me"];
  const repop = (el: HTMLElement) => { el.classList.remove("pop"); void el.offsetWidth; el.classList.add("pop"); };
  return {
    desc(t: string) { const d = q(".desc"); d.textContent = t; d.classList.remove("ph"); },
    amount(v: string) { const a = q(".amt"); if (a.dataset.v === v) return; a.dataset.v = v; a.className = "chip amt pop"; a.innerHTML = `<span class="mono">${v}</span>`; },
    date(t: string) { const d = q(".date"); (d.querySelector("span") as HTMLElement).textContent = t; d.classList.add("hot"); repop(d); },
    add(k: PersonKey) {
      if (people.includes(k)) return;
      people.push(k);
      q(".avs").insertAdjacentHTML("beforeend", av(k, "pop"));
      q(".n").textContent = String(people.length);
      q(".names").textContent = people.map((p) => PEOPLE[p].n).join(", ");
    },
    each(v: string) { const e = q(".each"); e.innerHTML = v; repop(e); },
    tag(html: string) { q(".tagslot").innerHTML = html; },
  };
}

export type HeardFrom = "instagram" | "tiktok" | "friend" | "x" | "youtube" | "facebook" | "reddit" | "google" | "store" | "ai" | "other";

// Brand colour per pill; blank = the ink colour (black brand marks).
const HEARD_FROM: [HeardFrom, string, string][] = [
  ["instagram", "Instagram", "#FF0069"], ["tiktok", "TikTok", ""], ["friend", "A friend", ""],
  ["x", "X (Twitter)", ""], ["youtube", "YouTube", "#FF0000"], ["facebook", "Facebook", "#0866FF"],
  ["reddit", "Reddit", "#FF4500"], ["google", "Google", "#4285F4"], ["ai", "ChatGPT / AI", ""],
];
const shuffle = <T,>(a: T[]) => { const b = a.slice(); for (let i = b.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [b[i], b[j]] = [b[j], b[i]]; } return b; };

export function buildSlides(opts: { member: boolean; ios: boolean; meadow: string }): Slide[] {
  return [
    {
      // The painted meadow (friends at dinner, receipts drifting up) fading into
      // the app's beige, with the logo and name underneath. Receipts keep rising
      // and fireflies twinkle; it moves on by itself, or on a tap.
      key: "splash",
      html: () => `<div class="sa">
        <div class="sa-img" style="background-image:url('${opts.meadow}')"></div>
        <canvas class="sa-back"></canvas><div class="sa-grain"></div><canvas class="sa-front"></canvas>
        <div class="sa-brand"><div class="sa-tile">${LOGO}</div>
          <div class="sa-word">${"Spliiit".split("").map((ch, k) => `<span style="transition-delay:${0.45 + k * 0.07}s">${ch}</span>`).join("")}</div>
          <p class="sa-tag">Splitting, handled.</p></div></div>`,
      async run(c) {
        // Wait (briefly) for the painting so it fades in whole, not in strips.
        const art = new Image();
        art.src = opts.meadow;
        await Promise.race([art.decode().catch(() => {}), new Promise((r) => setTimeout(r, 1500))]);
        await c.later(0);
        const sa = c.q(".sa");
        sa.classList.add("in");
        c.onLeave(startMeadow(sa));
        await c.later(4300);
      },
    },
    {
      key: "adding", dots: true, h: "Just say it. Spliiit does the math.",
      ticks: ["Type it", "Say it", "12 currencies", "Snap the receipt", "Date read off the receipt"],
      html: () => `<div class="stack"><div class="slot"></div>
        <div class="pill"><p class="ptext ph">Tell Spliiit anything…</p><span class="circ mic">${IC.mic}</span><span class="circ send">${BARS(false)}</span></div>
        <p class="listen">Listening… tap the mic to stop</p></div>`,
      async run(c) {
        const slot = c.q(".slot"), pt = c.q(".ptext"), mic = c.q(".mic"), send = c.q(".send"), pl = c.q(".pill"), listen = c.q(".listen");
        const setPill = (t: string, caret: boolean) => {
          const shown = t.length > 24 ? "…" + t.slice(-23) : t;
          pt.classList.toggle("ph", !t && !caret);
          pt.innerHTML = t || caret ? `${shown}${caret ? '<span class="caret"></span>' : ""}` : "Tell Spliiit anything…";
          pl.classList.toggle("live", !!t || caret);
        };
        const sendState = (ready: boolean) => { send.className = "circ send" + (ready ? " ink" : ""); send.innerHTML = ready ? IC.up : BARS(false); };
        const newCard = () => { slot.innerHTML = expenseCard(); const el = slot.firstElementChild as HTMLElement; nextFrame(() => el.classList.add("in")); return cardApi(el); };
        const pressSend = async (card: ReturnType<typeof cardApi>) => {
          send.classList.add("press"); await c.later(170); send.classList.remove("press");
          card.tag(`<span class="ok-tag pop">${IC.check2}Added</span>`);
        };
        const clearCard = async () => {
          const el = slot.firstElementChild as HTMLElement | null;
          if (el) { el.style.transition = "opacity .3s, transform .4s"; el.style.opacity = "0"; el.style.transform = "translateY(-8px)"; }
          await c.later(320); slot.innerHTML = "";
        };

        // 1 · Type it: letters in the pill, the card builds word by word.
        const phrase = "dinner at Pai 84 with sarah and marcus";
        await c.later(500);
        let card: ReturnType<typeof cardApi> | null = null;
        const onWords = (n: number) => {
          if (!card) card = newCard();
          if (n >= 1) card.desc("Dinner");
          if (n >= 3) card.desc("Dinner at Pai");
          if (n >= 4) card.amount("$84.00");
          if (n >= 6) { card.add("s"); card.each(money(42)); }
          if (n >= 8) { card.add("m"); card.each(money(28)); }
        };
        for (let i = 1; i <= phrase.length; i++) {
          setPill(phrase.slice(0, i), true);
          if (phrase[i] === " " || i === phrase.length) onWords(phrase.slice(0, i).split(" ").length);
          await c.later(phrase[i - 1] === " " ? 110 : 60);
        }
        sendState(true); setPill(phrase, false);
        await c.later(700); await pressSend(card!); c.tick(0);
        await c.later(1300);
        for (let i = phrase.length - 1; i >= 0; i--) { setPill(phrase.slice(0, i), false); await c.later(12); }
        sendState(false); setPill("", false); await clearCard();

        // 2 · Say it: the mic listens, words arrive, the card builds.
        await c.later(350);
        mic.className = "circ mic live"; mic.innerHTML = BARS(true); send.style.display = "none"; listen.classList.add("on"); pl.classList.add("live");
        const words = "gelato 12 euros with sarah".split(" ");
        await c.later(600);
        card = newCard();
        for (let i = 1; i <= words.length; i++) {
          setPill(words.slice(0, i).join(" "), false); pl.classList.add("live");
          if (i === 1) card.desc("Gelato");
          if (i === 3) { card.amount("€12.00"); c.tick(2); }
          if (i === 5) { card.add("s"); card.each("€6.00"); }
          await c.later(360);
        }
        await c.later(350);
        mic.className = "circ mic"; mic.innerHTML = IC.mic; send.style.display = ""; listen.classList.remove("on"); sendState(true);
        await c.later(600); await pressSend(card); c.tick(1);
        await c.later(1200);
        setPill("", false); sendState(false); await clearCard();

        // 3 · Snap the receipt: a scan sweeps it, lines light up, the date comes off it.
        mic.innerHTML = IC.camera; mic.classList.add("live"); pt.textContent = "Reading your receipt…"; pt.classList.add("ph");
        slot.innerHTML = `<div class="rc rise"><div class="scan"></div><div class="rt">PAI NORTHERN THAI</div><div class="rd">WED SEP 24 · 8:41 PM</div>
          <div class="ln"><span>Khao soi</span><span>19.00</span></div><div class="ln"><span>Pad thai</span><span>18.50</span></div>
          <div class="ln"><span>Green curry</span><span>21.00</span></div><div class="ln"><span>Spring rolls</span><span>9.50</span></div>
          <div class="ln"><span>Tax &amp; tip</span><span>16.00</span></div><div class="ln tot"><span>Total</span><span>84.00</span></div></div>`;
        const rc = slot.firstElementChild as HTMLElement;
        nextFrame(() => rc.classList.add("in"));
        await c.later(500);
        rc.querySelector(".scan")!.classList.add("go");
        await c.later(250); rc.querySelector(".rd")!.classList.add("hit");
        for (const l of Array.from(rc.querySelectorAll(".ln"))) { await c.later(230); l.classList.add("hit"); }
        c.tick(3);
        await c.later(700);
        rc.style.transition = "opacity .3s, transform .45s"; rc.style.opacity = "0"; rc.style.transform = "scale(.94)";
        await c.later(300);
        card = newCard(); mic.classList.remove("live"); mic.innerHTML = IC.mic; pt.textContent = "Tell Spliiit anything…";
        await c.later(200); card.desc("Pai Northern Thai");
        await c.later(250); card.amount("$84.00");
        await c.later(350); card.date("Sep 24 · from receipt"); c.tick(4);
        await c.later(400); card.add("s"); await c.later(180); card.add("m"); card.each(money(28));
      },
    },
    {
      key: "splitting", dots: true, h: "Split it any way you like.",
      ticks: ["Equal, % or exact", "Add people before they join", "Rent repeats itself", "Fewer payments"],
      html: () => `<div class="stack">
        <div class="card rise sc">
          <div class="head"><span class="hic">${IC.receipt}</span><span class="hl">Expense</span></div>
          <p class="desc">Rent</p>
          <div class="chips"><span class="chip"><span class="mono">$2,400.00</span></span><span class="chip">${IC.wallet}Paid by you</span><span class="rep"></span></div>
          <div class="seg"><i class="knob"></i><span class="on">Equally</span><span>By %</span><span>Exact</span></div>
          <div class="prow">${av("me")}<span class="nm">You</span><span class="pct">40%</span><span class="amt">$800.00</span></div>
          <div class="prow">${av("s")}<span class="nm">Sarah</span><span class="pct">35%</span><span class="amt">$800.00</span></div>
          <div class="prow"><span class="av ghost">J</span><span class="nm">Jordan<small>Not on Spliiit yet · invite sent</small></span><span class="pct">25%</span><span class="amt">$800.00</span></div>
        </div>
        <div class="card rise tc row" style="padding:14px 16px">
          <span class="hic">${IC.swap}</span>
          <span style="flex:1;font-size:14.5px">Simplify debts<small style="display:block;font-size:12px;color:var(--mfg);margin-top:1px"><span class="mono pays">6</span> payments in this group</small></span>
          <span class="tgl"></span>
        </div></div>`,
      async run(c) {
        const segs = c.qa(".seg span"), knob = c.q(".knob"), amts = Array.from(c.qa(".amt")), pcts = c.qa(".pct");
        const seg = (i: number) => { knob.style.transform = `translateX(${i * 100}%)`; segs.forEach((s, k) => s.classList.toggle("on", k === i)); };
        const count = async (targets: number[]) => {
          const from = amts.map((a) => Number((a.textContent || "0").replace(/[$,]/g, "")));
          const N = 14;
          for (let f = 1; f <= N; f++) {
            amts.forEach((a, k) => { a.textContent = money(Math.round(from[k] + (targets[k] - from[k]) * (1 - Math.pow(1 - f / N, 3)))); });
            await c.later(28);
          }
        };
        await c.later(300); c.q(".sc").classList.add("in");
        await c.later(1300); seg(1); pcts.forEach((p) => p.classList.add("on")); await count([960, 840, 600]);
        await c.later(1100); seg(2); pcts.forEach((p) => p.classList.remove("on")); await count([1000, 800, 600]);
        c.tick(0);
        await c.later(700); c.q(".nm small").classList.add("on"); c.tick(1);
        await c.later(1000); c.q(".rep").outerHTML = `<span class="chip ok pop">${IC.repeat}Every month</span>`; c.tick(2);
        await c.later(1000); c.q(".tc").classList.add("in");
        await c.later(700); c.q(".tgl").classList.add("on");
        const pays = c.q(".pays");
        for (const n of [5, 4, 3, 2]) { await c.later(170); pays.textContent = String(n); }
        c.tick(3);
      },
    },
    {
      key: "getting-paid", dots: true, h: "Get paid back. No awkward texts.",
      ticks: ["Reminders sent for you", "Your pay details, shared", "Interac, PayPal, Venmo, Wise +5", "One tap to settle"],
      html: () => `<div class="stack">
        <div class="notif"><div class="row" style="gap:8px"><span class="nic"><svg width="14" height="14" viewBox="0 0 32 32" fill="none" aria-hidden="true"><path d="M10 11V23M16 11V23M22 11V23" stroke="currentColor" stroke-width="3.4" stroke-linecap="round"/></svg></span><span style="font-size:12.5px;font-weight:600;flex:1">Spliiit</span><span style="font-size:11.5px;color:var(--mfg)">now</span></div>
          <div style="font-size:13.5px;line-height:1.4;margin-top:6px">Friendly nudge: you owe Abhishek <span class="mono">$28.00</span> for Dinner at Pai.</div></div>
        <div class="card rise pc" style="padding:14px 16px 12px">
          <div class="cap">How Abhishek gets paid</div>
          <div class="mrow rise r1" style="margin-top:4px"><span class="k">Interac e-Transfer<small>abhi@mail.com</small></span><span class="cp c1">${IC.copy}Copy</span></div>
          <div class="mrow rise r2"><span class="k">PayPal<small>@abhi</small></span><span class="cp">${IC.copy}Copy</span></div>
          <p class="more"></p>
        </div>
        <div class="card rise oc row" style="padding:12px 14px">${av("a")}<span style="flex:1;font-size:14px" class="ot"><span class="ol">You owe Abhishek</span><span class="mono" style="display:block;font-size:20px;margin-top:1px">$28.00</span></span><span class="sbtn">Settle up</span></div>
      </div>`,
      async run(c) {
        await c.later(400); c.q(".notif").classList.add("in"); c.tick(0);
        await c.later(1200); c.q(".pc").classList.add("in");
        await c.later(350); c.q(".r1").classList.add("in"); await c.later(200); c.q(".r2").classList.add("in");
        await c.later(700);
        const cp = c.q(".c1"); cp.style.transform = "scale(.92)"; await c.later(150); cp.style.transform = ""; cp.classList.add("done"); cp.innerHTML = `${IC.check2}Copied`; c.tick(1);
        await c.later(500);
        const more = c.q(".more"), txt = "Also: Venmo · Wise · Revolut · Zelle · Cash App · bank".split(" ");
        for (let i = 1; i <= txt.length; i++) { more.textContent = txt.slice(0, i).join(" "); await c.later(90); }
        c.tick(2);
        await c.later(700); c.q(".oc").classList.add("in");
        await c.later(1000);
        const b = c.q(".sbtn"); b.classList.add("press"); await c.later(170); b.classList.remove("press");
        const amt = c.q(".ot .mono");
        for (let f = 1; f <= 16; f++) { amt.textContent = money(28 * Math.pow(1 - f / 16, 2)); await c.later(30); }
        c.q(".ol").textContent = "All settled with Abhishek";
        b.outerHTML = `<span class="ok-tag pop">${IC.check2}Settled</span>`; c.tick(3);
      },
    },
    {
      key: "trust", dots: true, h: "Every change, on the record.",
      ticks: ["Before and after", "Spent vs posted date", "Deleted? Restore it", "Same history for everyone"],
      html: () => `<div class="stack">
        <div class="card rise hc" style="padding:12px 16px 6px">
          <div class="row" style="margin-bottom:2px"><span class="cap" style="flex:1">Roommates · History</span><span class="viewers">${av("me")}${av("s")}${av("m")}</span></div>
          <div class="hrow g"><span class="t"><b>Groceries</b><small>Sarah paid · Sep 20</small></span><span class="tgs"></span><span class="v gv">$64.20</span></div>
          <div class="hrow p"><span class="t"><b>Pizza night</b><small>Marcus paid · Sep 23</small></span><span class="tgp"></span><span class="v">$42.00</span></div>
          <div class="hrow"><span class="t"><b>Hydro</b><small>You paid · Sep 18</small></span><span class="v">$88.10</span></div>
        </div>
        <div class="card rise dc" style="padding:14px 16px">
          <div class="row"><span class="cap" style="flex:1">What changed</span><span style="font-size:12px;color:var(--mfg)">Sarah · Sep 25</span></div>
          <div class="wc"><span></span><span class="h">Before</span><span class="h">After</span>
            <span class="k">Amount</span><span class="b mono">$64.20</span><span class="mono">$68.40</span>
            <span class="k">Spent on</span><span class="b">Sep 21</span><span>Sep 20</span></div>
          <div class="lock">${IC.lock}Added Sep 22 · can't be changed</div>
        </div>
      </div>`,
      async run(c) {
        await c.later(300); c.q(".hc").classList.add("in");
        await c.later(1000);
        const gv = c.q(".gv"); gv.textContent = "$68.40"; gv.classList.add("pop"); c.q(".tgs").innerHTML = `<span class="tg e pop">Edited</span>`;
        await c.later(700);
        const g = c.q(".hrow.g"); g.classList.add("press"); await c.later(260); g.classList.remove("press");
        c.q(".dc").classList.add("in");
        const cells = Array.from(c.qa(".wc > *"));
        await c.later(350); [0, 1, 2].forEach((k) => cells[k].classList.add("in"));
        for (const row of [[3, 4, 5], [6, 7, 8]]) {
          await c.later(380); cells[row[0]].classList.add("in"); cells[row[1]].classList.add("in");
          await c.later(300); cells[row[1]].classList.add("x");
          await c.later(220); cells[row[2]].classList.add("in");
          if (row[0] === 3) c.tick(0);
        }
        await c.later(500); c.q(".lock").classList.add("in"); c.tick(1);
        await c.later(1300);
        const p = c.q(".hrow.p"); p.classList.add("del"); c.q(".tgp").innerHTML = `<span class="tg d pop">Deleted</span>`;
        await c.later(900); c.q(".tgp").innerHTML = `<span class="rst pop">${IC.undo}Restore</span>`;
        await c.later(800); c.q(".rst").classList.add("press"); await c.later(170);
        p.classList.remove("del"); c.q(".tgp").innerHTML = ""; c.tick(2);
        await c.later(800); c.q(".viewers").classList.add("in"); c.tick(3);
      },
    },
    {
      key: "every-little-thing", dots: true, h: "And every little thing.", sub: "The stuff you'd only notice if it was missing.",
      html: () => `<div class="stack" style="align-items:center">
        <div class="cloud">${([
          ["Import from Splitwise", 1], ["Export to a spreadsheet"], ["Receipts never stored"], ["Unlimited expenses"], ["Unlimited scans"],
          ["Talk to it, hands-free", 1], ["Invite by link"], ["Group admins"], ["Approve who joins"], ["Pick your notifications"],
          ["Weekly balance email"], ["Restore deleted", 1], ["Leave a group anytime"], [opts.ios ? "Sign in with Apple" : "Sign in with Google"], ["Delete your account anytime"], ["Rename groups"],
        ] as [string, number?][]).map(([t, k]) => `<span class="${k ? "k" : ""}">${t}</span>`).join("")}</div>
        <div class="count rise"><span class="cn">0</span><span class="pl" style="opacity:0;transition:opacity .3s">+</span> little things<small>already taken care of</small></div></div>`,
      async run(c) {
        await c.later(350);
        const chips = Array.from(c.qa(".cloud span"));
        for (const i of [0, 5, 11, 2, 8, 13, 1, 6, 14, 3, 9, 12, 4, 7, 10, 15]) { chips[i].classList.add("in"); await c.later(110); }
        c.q(".count").classList.add("in");
        const cn = c.q(".cn");
        for (let n = 1; n <= 30; n++) { cn.textContent = String(n); await c.later(18 + n * 1.2); }
        c.q(".pl").style.opacity = "1";
      },
    },
    {
      // Where did you hear about us? Required: the sign-in buttons stay locked
      // until they pick one. Options are shuffled (Other stays last) so the
      // first chip doesn't collect lazy taps.
      key: "last",
      html: () => {
        const buttons = opts.member
          ? `<button class="cta rise b1" type="button" data-finish="done" data-testid="intro-finish">Start splitting</button>`
          : (opts.ios
              ? `<button class="cta rise b1" type="button" data-finish="apple" data-testid="intro-apple">${IC.apple}Continue with Apple</button>
                 <button class="cta alt rise b2" type="button" data-finish="google" data-testid="intro-google">${IC.google}Continue with Google</button>`
              : `<button class="cta rise b1" type="button" data-finish="google" data-testid="intro-google">${IC.google}Continue with Google</button>`)
            + `<button class="cta alt rise b2" type="button" data-finish="email" data-testid="intro-email">Use email instead</button>`
            + `<button class="link rise b3" type="button" data-finish="login" data-testid="intro-login">Already have an account? <b>Sign in</b></button>`;
        const store: [HeardFrom, string, string] = ["store", opts.ios ? "App Store" : "Google Play", opts.ios ? "#0D96F6" : ""];
        const picks = shuffle(HEARD_FROM.concat([store]));
        const check = `<span class="hck"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg></span>`;
        const pill = ([v, t, bc]: [HeardFrom, string, string]) =>
          `<button class="hp" type="button" role="radio" aria-checked="false" data-src="${v}" data-testid="intro-heard-${v}"${bc ? ` style="--bc:${bc}"` : ""}><span class="hgl">${v === "store" && !opts.ios ? BRAND.play : BRAND[v as keyof typeof BRAND]}</span>${t}${check}</button>`;
        // Other holds its own text field; tapping it opens a typing view (see intro.css .typing).
        const other = `<div class="hp oth" role="radio" aria-checked="false" tabindex="0" data-src="other" data-testid="intro-heard-other"><span class="hgl">${BRAND.other}</span><span class="hlbl">Other</span><input class="other" type="text" maxlength="60" enterkeyhint="done" placeholder="Where? (optional)" aria-label="Where did you hear about us?" tabindex="-1" />${check}</div>`;
        return `<div class="hstage"><div class="hbox"><div class="hmid">
            <div class="logo">${LOGO}</div>
            <div class="hcloud" role="radiogroup" aria-label="Where did you hear about us?">${picks.map(pill).join("")}${other}</div>
            <p class="donehint">Optional. Tap Done when you're finished.</p>
          </div></div></div>
          <div class="copy hcopy"><h3>${"One last thing before you start splitting.".split(" ").map((w, j) => `<span class="w" style="transition-delay:${j * 55}ms">${w}</span>`).join(" ")}</h3><p class="sub">Where did you hear about us?</p></div>
          <div class="su locked">${buttons}</div>`;
      },
      async run(c) {
        // Like the other slides, the stage scales down to fit shorter phones.
        const stage = c.q(".hstage"), box = c.q(".hbox");
        const fit = () => {
          box.style.width = `${Math.min(357, stage.clientWidth - 36)}px`;
          box.style.transform = `scale(${Math.max(0.6, Math.min(1, (stage.clientHeight - 8) / box.offsetHeight))})`;
        };
        fit();
        const ro = new ResizeObserver(fit); ro.observe(stage);
        c.onLeave(() => ro.disconnect());
        // Same build-up as the rest of the tour: the logo draws, the headline
        // rises word by word, the pills pop in one by one, then sign-in.
        await c.later(150); c.q(".logo").classList.add("in");
        await c.later(500);
        for (const o of Array.from(c.qa(".hp"))) { o.classList.add("in"); await c.later(80); }
        await c.later(250);
        for (const b of Array.from(c.qa(".su .rise"))) { b.classList.add("in"); await c.later(110); }
      },
    },
  ];
}
